/**
 * Date Selector Component.
 * This Provide feature to choose the Date using Kepler's Input and Calendar Component.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import FormField from 'kepler/lib/components/FormField';
import Glyph from 'kepler/lib/components/Glyph';
import Input from 'kepler/lib/components/Input';
import Calendar from 'kepler/lib/components/Calendar';
import _get from 'lodash/get';

// Helpers.
import {
    isValidDate,
    getCurrentDate,
    getNormalizedDate,
    getFormattedDate,
    getShortMonthNames,
    getLongMonthNames,
    getShortDayNames,
} from '../../../lib/date-helper';
import * as shape from '../../constants/PropTypesShapes';

class DateSelector extends Component {

  // This Util Method helps to find the clicked element Parent.
  static findParentNode(targetNode, className) {
    let currentNode = targetNode.parentNode;
    let foundParent = false;

    if (!currentNode || (currentNode && typeof currentNode.getAttribute !== 'function')) {
      return false;
    }

    while (currentNode && currentNode.getAttribute && currentNode.getAttribute('class') !== className) {
      const classList = currentNode.getAttribute('class');

      if (classList && classList.indexOf(className) > -1) {
        foundParent = true;
        break;
      }
      currentNode = currentNode.parentNode;
    }

    return foundParent;
  }

  // Compare day, month and year to check future;
  static isFuture(givenDate, now) {
    if (givenDate instanceof Date && now instanceof Date) {
      return givenDate.getTime() > now.getTime() &&
      !(givenDate.getDate() === now.getDate() && givenDate.getFullYear() === now.getFullYear() && givenDate.getMonth() === now.getMonth());
    }
    return false;
  }

  // Get current formatDate
  static getCurrentFomattedDate() {
    const year = getCurrentDate().getUTCFullYear();
    const month = getCurrentDate().getUTCMonth() + 1;
    const day = getCurrentDate().getUTCDate();

    const currentDate = getFormattedDate(year, month, day);
    return currentDate;
  }

  // Set the Field Settings and Error as States on Construction.
  constructor(props) {
    super(props);

    const { options } = props;
    const now = new Date();

    this.state = {
      show: false,
      selectedDate: {
        start: {
          year: now.getYear(),
          month: now.getMonth(),
          date: now.getDate(),
        },
      },
      touched: false,
      position: 'bottom',
      options: Object.assign({}, options, { value: DateSelector.getCurrentFomattedDate() }),
      errors: [],
    };

    this.onDatePickerOutsideClick = this.onDatePickerOutsideClick.bind(this);
    this.setPosition = this.setPosition.bind(this);
  }

  // Trigger Once Component Mounted First Time.
  // Add Event Listener to Close the Date Picker on Click Outside Date Picker Element.
  componentDidMount() {
    document.addEventListener('mousedown', this.onDatePickerOutsideClick);

    const lastOnScroll = window.onscroll;
    window.onscroll = () => {
      if (lastOnScroll) {
        lastOnScroll();
      }

      this.setPosition();
    };
  }

  // Every Component Re-render with New Props.
  // This will update the Field Settings and validate according to the prop change during Topic Tree Selection.
  componentWillReceiveProps(props) {
    const { validate, options } = props;

    this.setState({
      options,
    });

    if (validate === true) {
      this.validate(options.value);
    }
  }

  // Trigger Once Component Un-mounted Finally.
  // Remove Event Listener which helps to Close the Date Picker on Click Outside Date Picker Element.
  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onDatePickerOutsideClick);
  }

  // Trigger on Click of Outside Date Picker Element.
  onDatePickerOutsideClick(event) {
    // Do Nothing, If its clicked on Calendar Input.
    if (event.target && event.target.getAttribute) {
      const id = event.target.getAttribute('id');
      const optionId = _get(this.props, 'options.id');
      if (id === optionId) {
        return true;
      }
    }

    // Check If its clicked outside the Date Picker.
    // if (event.target && DateSelector.findParentNode(event.target, 'date-picker-calendar-field') === false) {
    if (event.target && DateSelector.findParentNode(event.target, 'date-picker-calendar-field') === false) {
      this.handleOnCalendarBlur();
    }

    return true;
  }

  // Trigger, On Click of Calendar Icon.
  // Focus the Date Picker Input.
  onCalendarIconClick() {
    if (_get(this.props, 'options.id')) {
      const $input = document.getElementById(this.props.options.id);
      if ($input && $input.focus) {
        $input.focus();
      }
    }

    this.showDatePicker(true);
  }

  setPosition() {
    const $windowHeight = window.innerHeight;
    const $input = document.getElementById(this.props.options.id);
    const $calendar = document.getElementById(`picker-${this.props.options.id}`);
    const $inputHeight = ($input && $input.getBoundingClientRect) ? $input.getBoundingClientRect().top : 0;
    const $calendarHeight = ($calendar && $calendar.clientHeight) ? $calendar.clientHeight : 300;
    const freeSpace = ($windowHeight - $inputHeight);
    const position = (freeSpace < $calendarHeight) ? 'top' : 'bottom';

    if (position !== this.state.position) {
      this.setState({
        position,
      });
    }
  }

  // Send the Field State to Form.
  setFormState() {
    this.props.handleValue(this.state.options);
  }

  // Check, If Field Value is empty.
  isEmpty(value) {
    return (this.props.options.isRequired && value === '');
  }


  // Validate Field Value and Update the State.
  validate(value, show) {
    const errors = [];
    const options = this.props.options;
    const now = getCurrentDate();
    const givenDate = getNormalizedDate(value);
    const isCompareDateExist = (givenDate && givenDate.getDate && now.getDate);
    const isFutureDate = (isCompareDateExist && DateSelector.isFuture(givenDate, now));
    const isInValidDate = (givenDate) ? (!isValidDate(givenDate)) : true;

    if (this.isEmpty(value)) {
      errors.push(this.props.errorWarn.required);
      options.hasError = true;
      options.value = '';
    } else if (isFutureDate || isInValidDate) {
      errors.push(this.props.errorWarn.invalidDate);
      options.hasError = true;
      options.value = value;
    } else {
      options.hasError = false;
      options.value = value;
    }

    this.setState({
      show,
      options,
      errors,
    });
  }

  // Show the Date Picker Calendar on Input Focus.
  handleFocus() {
    this.setPosition();
    this.showDatePicker(true);
  }

  handleChange(value) {
    this.validate(value, false);
    this.setFormState();
  }

  // Validate and Save Form Data on Blur of Calendar.
  handleOnCalendarBlur() {
    const { touched, options: { value = '' } } = this.state;

    if (touched) {
      this.validate(value, false);
      this.setFormState();
    }
  }

  // Validate and Save Form Data on Calendar Date Selection.
  handleOnDateChange(value) {
    const selectedDate = value.start;
    const { year = '', month = '', day = '' } = selectedDate;

    if (year && month && day) {
      this.setState({
        selectedDate: value,
      });

      const formattedDate = getFormattedDate(year, month, day);
      this.validate(formattedDate, false);
      this.setFormState();
    }
  }

  showDatePicker(show) {
    this.setState({
      touched: true,
      show,
    });
  }

  hasError() {
    return (Array.isArray(this.state.errors) && this.state.errors.length > 0);
  }

  renderDatePicker() {
    if (this.state.show) {
      const pickerId = `picker-${this.props.options.id}`;
      const position = this.state.position;
      const defaultClass = `date-picker date-picker-calendar-field ${position}`;
      const pickerClass = (this.hasError()) ? `${defaultClass} error` : defaultClass;

      return (
        <div id={pickerId} className={pickerClass} tabIndex="0" onBlur={() => this.handleOnCalendarBlur()}>
          <Calendar
            type="datePicker"
            date={this.state.calendarDate}
            localeLabels={{
              shortMonth: getShortMonthNames(),
              month: getLongMonthNames(),
              day: getShortDayNames(),
            }}
            onChange={value => this.handleOnDateChange(value)}
          />
        </div>
      );
    }
    return null;
  }

  // Render the `DateSelector` Component.
  render() {
    return (
      <FormField className="DatePickerBlock">
        <label htmlFor={this.props.options.id} className="formLabel">
          {this.props.options.id}
        </label>
        <Input
          onFocus={() => this.handleFocus()}
          onChange={value => this.handleChange(value)}
          type="text"
          placeholder={this.props.options.label}
          className="date-picker-input"
          id={this.props.options.id}
          name={this.props.options.id}
          value={this.state.options.value}
          errors={this.state.errors}
        />
        <Glyph icon="icon-calendar" onClick={() => this.onCalendarIconClick(true)} />
        {this.renderDatePicker()}
      </FormField>
    );
  }
}

// FormButtons Props Types.
DateSelector.propTypes = {
  // Simple Props.
  validate: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types

  // Complex Props.
  options: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    isRequired: PropTypes.bool,
    value: PropTypes.string,
    hasError: PropTypes.bool,
  }),

  // Method Props.
  handleValue: PropTypes.func.isRequired,
  errorWarn: PropTypes.shape(shape.WEBFORM_ERRORS),
};

// EmailUs Props default values.
DateSelector.defaultProps = {
  validate: false,
  options: {
    id: '',
    type: '',
    label: '',
    name: '',
    isRequired: false,
    value: '',
    hasError: false,
  },
  errorWarn: {},
};

export default DateSelector;
